import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import { API_BASE_URL, getSession } from "./apiConfig";

const SP_API = `${API_BASE_URL}/emp`;

const CreateEmployee = () => {
  const session = getSession();
  const userRole = session?.Role || "";

  const [activeTab, setActiveTab] = useState("entry");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [empData, setEmpData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);

  const emptyFormState = {
    Client_ID: "",
    Employee_ID: "",
    Employee_Name: "",
  };
  const [formData, setFormData] = useState(emptyFormState);
  const [editingId, setEditingId] = useState(null);

  // Load records via the stored-procedure GET endpoint
  const loadRecords = async () => {
    setIsFetching(true);
    try {
      const res = await fetch(SP_API);
      if (!res.ok) throw new Error("Failed to fetch employee records (SP).");
      const data = await res.json();
      setEmpData(data);
    } catch (err) {
      console.error("Load Error:", err);
      setMessage({ type: "error", text: err.message });
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData(emptyFormState);
    setEditingId(null);
  };

  // Insert (POST) or Update (PUT) via the matching endpoint. Create still
  // goes through the stored-procedure POST endpoint (unchanged); Update uses
  // the new plain-EF PUT endpoint added to EmployeeController.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: "", text: "" });

    const isEditing = editingId !== null;

    // Format payload strictly before sending
    const payload = {
      Client_ID: formData.Client_ID ? Number(formData.Client_ID.trim()) : null,
      Employee_ID: formData.Employee_ID.trim(),
      Employee_Name: formData.Employee_Name.trim(),
    };

    try {
      const response = await fetch(
        isEditing ? `${SP_API}/${editingId}` : SP_API,
        {
          method: isEditing ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        // Read as text first to prevent the "stream already read" error
        const errorText = await response.text();
        let errorMessage = `Server Error: ${response.status} ${response.statusText}`;

        try {
          // Attempt to parse text as JSON
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (parseErr) {
          // If it's not JSON, fallback to raw text
          if (errorText) {
            errorMessage = `Server responded with: ${errorText.substring(0, 150)}`;
          }
        }

        throw new Error(errorMessage);
      }

      setMessage({
        type: "success",
        text: isEditing
          ? "Employee updated successfully."
          : "Employee Created Successfully via Stored Procedure!",
      });

      resetForm();

      // Refresh the list
      loadRecords();
    } catch (err) {
      console.error("API POST Error:", err);
      setMessage({
        type: "error",
        text:
          err.message === "Failed to fetch"
            ? "Network error: check the backend is running and reachable."
            : err.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // `item` here comes from GET /api/emp (sp_get_emp()), whose JSON casing is
  // inconsistent -- follow the same defensive dual-check the View table
  // already uses (item.client_ID || item.Client_ID, etc.)
  const handleEdit = (item) => {
    setFormData({
      Client_ID: String(item.client_ID ?? item.Client_ID ?? ""),
      Employee_ID: String(item.employee_ID ?? item.Employee_ID ?? ""),
      Employee_Name: item.employee_Name ?? item.Employee_Name ?? "",
    });
    setEditingId(item.id);
    setMessage({ type: "", text: "" });
    setActiveTab("entry");
  };

  const handleDelete = async (item) => {
    if (userRole !== "Admin") {
      setMessage({ type: "error", text: "Only Admin users can delete records." });
      return;
    }
    const name = item.employee_Name ?? item.Employee_Name ?? "this employee";
    if (!window.confirm(`Delete employee "${name}"?`)) return;

    try {
      const res = await fetch(`${SP_API}/${item.id}`, {
        method: "DELETE",
        headers: { "X-User-Role": userRole },
      });
      if (!res.ok) throw new Error("Delete failed");
      setMessage({ type: "success", text: "Employee deleted successfully." });
      loadRecords();
    } catch (err) {
      setMessage({ type: "error", text: "Error deleting employee." });
    }
  };

  return (
    <AdminLayout empData={empData}>
      <style>{`
        .page-header { margin-bottom: 24px; }
        .page-title { font-size: 1.5rem; color: #0f172a; font-weight: 700; margin: 0; }
        .content-card { background: #ffffff; border-radius: 12px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); border: 1px solid #f1f5f9; overflow: hidden; }
        .tab-container { display: flex; background: #f8fafc; border-bottom: 1px solid #e2e8f0; padding: 0 16px; }
        .tab-btn { padding: 16px 24px; background: transparent; border: none; border-bottom: 2px solid transparent; font-weight: 600; font-size: 0.9rem; color: #64748b; cursor: pointer; transition: all 0.2s ease; }
        .tab-btn:hover { color: #0f172a; }
        .tab-btn.active { color: #2563eb; border-bottom-color: #2563eb; }
        .form-wrapper { padding: 32px; }
        .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 24px; }
        .form-group { display: flex; flex-direction: column; gap: 8px; }
        .form-group label { font-size: 0.875rem; font-weight: 600; color: #475569; }
        .form-control { width: 100%; padding: 10px 14px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 0.95rem; color: #1e293b; box-sizing: border-box; }
        .form-control:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15); }
        .btn-submit { margin-top: 32px; padding: 12px 28px; background: #2563eb; color: white; border: none; border-radius: 8px; font-weight: 600; font-size: 0.95rem; cursor: pointer; }
        .btn-submit:hover:not(:disabled) { background: #1d4ed8; }
        .btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }
        .btn-secondary-sm { padding: 8px 16px; background: #f1f5f9; color: #334155; border: 1px solid #cbd5e1; border-radius: 8px; font-weight: 600; font-size: 0.85rem; cursor: pointer; }
        .btn-secondary-sm:hover { background: #e2e8f0; }
        .btn-secondary-sm:disabled { opacity: 0.7; cursor: not-allowed; }
        .alert { padding: 12px 16px; border-radius: 8px; margin-bottom: 24px; font-weight: 500; font-size: 0.9rem; }
        .alert-success { background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; }
        .alert-error { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }
        .table-wrap { overflow-x: auto; width: 100%; }
        .data-table { width: 100%; border-collapse: collapse; text-align: left; }
        .data-table th { background: #f8fafc; padding: 14px 24px; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; font-weight: 700; border-bottom: 1px solid #e2e8f0; }
        .data-table td { padding: 16px 24px; font-size: 0.9rem; color: #334155; border-bottom: 1px solid #e2e8f0; }
        .data-table tbody tr:hover { background: #f1f5f9; }
        .empty-state { text-align: center; padding: 48px 24px; color: #64748b; }
      `}</style>

      <div className="page-header">
        <h1 className="page-title">Emp Management (Stored Procedure)... 37</h1>
      </div>

      <div className="content-card">
        <div className="tab-container">
          <button
            className={`tab-btn ${activeTab === "entry" ? "active" : ""}`}
            onClick={() => setActiveTab("entry")}
          >
            {editingId !== null ? "Edit Entry" : "Create New Entry (SP)"}
          </button>
          <button
            className={`tab-btn ${activeTab === "view" ? "active" : ""}`}
            onClick={() => setActiveTab("view")}
          >
            View Records ({empData.length})
          </button>
        </div>

        {activeTab === "entry" && (
          <div className="form-wrapper">
            {message.text && (
              <div
                className={`alert ${message.type === "success" ? "alert-success" : "alert-error"}`}
              >
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Client ID *</label>
                  <input
                    name="Client_ID"
                    type="number"
                    className="form-control"
                    placeholder="e.g., 12345678"
                    value={formData.Client_ID}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Employee ID *</label>
                  <input
                    name="Employee_ID"
                    type="number"
                    className="form-control"
                    placeholder="e.g., 1234"
                    value={formData.Employee_ID}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Employee Name *</label>
                  <input
                    name="Employee_Name"
                    className="form-control"
                    placeholder="e.g., John Doe"
                    value={formData.Employee_Name}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <button type="submit" className="btn-submit" disabled={isLoading}>
                {isLoading
                  ? "Saving Record..."
                  : editingId !== null
                    ? "Update Employee Record"
                    : "Save Employee Record (via SP)"}
              </button>
              {editingId !== null && (
                <button
                  type="button"
                  className="btn-secondary-sm"
                  style={{ marginLeft: "10px" }}
                  onClick={resetForm}
                  disabled={isLoading}
                >
                  Cancel Edit
                </button>
              )}
            </form>
          </div>
        )}

        {activeTab === "view" && (
          <div className="table-wrap">
            {isFetching ? (
              <div className="empty-state">Loading records...</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client ID</th>
                    <th>Employee ID</th>
                    <th>Employee Name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {empData.length > 0 ? (
                    empData.map((item, idx) => (
                      <tr key={item.id || idx}>
                        <td>
                          <strong>
                            {item.client_ID || item.Client_ID || "-"}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {item.employee_ID || item.Employee_ID || "-"}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {item.employee_Name || item.Employee_Name || "-"}
                          </strong>
                        </td>
                        <td>
                          <button className="btn-secondary-sm" onClick={() => handleEdit(item)}>Edit</button>
                          <button
                            className="btn-secondary-sm"
                            style={{ marginLeft: "6px", color: "#dc2626", borderColor: "#fecaca" }}
                            onClick={() => handleDelete(item)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="empty-state">
                        <div
                          style={{ marginBottom: "8px", fontSize: "1.2rem" }}
                        >
                          📦
                        </div>
                        No Employee records found in the database.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CreateEmployee;